import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

import { GravunCard } from "../data-display/SectionCard";

/** Shared building blocks for the report panels. */

export const StatGrid = ({ children }: { children: ReactNode }) => (
  // 2 columns on a phone rather than 1: KPI values are short, and a single
  // column pushes the interesting numbers below the fold. Four columns only
  // from `lg`, at 768px a four-up row of currency values cannot shrink far
  // enough and pushes the whole page sideways. `min-w-0` on the children is
  // the other half of that fix: grid items default to `min-width:auto`.
  <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 [&>*]:min-w-0">
    {children}
  </div>
);

export const Stat = ({
  label,
  value,
  helper,
  tone,
}: {
  label: string;
  value: string;
  helper?: string;
  tone?: "default" | "positive" | "negative";
}) => (
  <GravunCard className="px-4 py-3.5">
    <div className="text-[10.5px] font-semibold uppercase tracking-[0.09em] text-muted-foreground">
      {label}
    </div>
    <div
      className={cn(
        "mt-1.5 truncate text-[22px] font-semibold leading-7 tracking-tight",
        tone === "positive" && "text-emerald-700",
        tone === "negative" && "text-destructive",
        (!tone || tone === "default") && "text-foreground",
      )}
    >
      {value}
    </div>
    {helper ? (
      <div className="mt-0.5 truncate text-[12px] leading-4 text-muted-foreground">
        {helper}
      </div>
    ) : null}
  </GravunCard>
);

/**
 * A table that scrolls inside its own card instead of widening the page, * the fix that keeps every report usable at 390px.
 */
export const ReportTable = ({
  columns,
  rows,
  empty = "Nothing in this period.",
  align,
}: {
  columns: string[];
  rows: ReactNode[][];
  empty?: string;
  align?: ("left" | "right")[];
}) => {
  if (!rows.length) {
    return (
      <p className="py-6 text-center text-[13px] text-muted-foreground">
        {empty}
      </p>
    );
  }
  return (
    <div className="-mx-1 overflow-x-auto px-1">
      <table className="w-full min-w-[420px] border-collapse text-[13px]">
        <thead>
          <tr>
            {columns.map((column, index) => (
              <th
                key={column}
                className={cn(
                  "border-b border-border pb-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground",
                  align?.[index] === "right" ? "text-right" : "text-left",
                )}
              >
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr
              key={rowIndex}
              className="border-b border-border-subtle last:border-0"
            >
              {row.map((cell, cellIndex) => (
                <td
                  key={cellIndex}
                  className={cn(
                    "py-2 text-foreground",
                    align?.[cellIndex] === "right"
                      ? "text-right tabular-nums"
                      : "text-left",
                  )}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/**
 * Horizontal bar list, proportion at a glance without a charting library.
 * Bars are scaled to the largest value, so the shape is comparative, not
 * absolute; the number beside each bar carries the real magnitude.
 */
export const BarList = ({
  items,
  empty = "No data in this period.",
}: {
  items: { label: string; value: number; display: string; sub?: string }[];
  empty?: string;
}) => {
  if (!items.length) {
    return (
      <p className="py-6 text-center text-[13px] text-muted-foreground">
        {empty}
      </p>
    );
  }
  const max = Math.max(...items.map((item) => item.value), 1);
  return (
    <div className="flex flex-col gap-2.5">
      {items.map((item) => (
        <div key={item.label} className="min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <span className="truncate text-[13px] font-medium text-foreground">
              {item.label}
            </span>
            <span className="shrink-0 text-[13px] tabular-nums text-muted-foreground">
              {item.display}
            </span>
          </div>
          <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-primary"
              style={{ width: `${Math.max(2, (item.value / max) * 100)}%` }}
            />
          </div>
          {item.sub ? (
            <div className="mt-0.5 text-[11.5px] text-muted-foreground">
              {item.sub}
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
};

export const ReportSection = ({
  title,
  subtitle,
  children,
  className,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  className?: string;
}) => (
  <GravunCard className={cn("min-w-0 px-5 py-4", className)}>
    <h2 className="text-[14px] font-semibold leading-5 tracking-tight text-foreground">
      {title}
    </h2>
    {subtitle ? (
      <p className="mb-3 text-[12px] leading-4 text-muted-foreground">
        {subtitle}
      </p>
    ) : (
      <div className="mb-3" />
    )}
    {children}
  </GravunCard>
);
